import util from '../util';
import Widget from './Widget';

const AVATAR_SVG = `<svg fill="#eee9f0" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
<path d="m 256,17.712357 c 60.75077,0 109.99959,40.491541 109.99959,112.288053 0,71.79651 -49.24882,130.00041 -109.99959,130.00041 -60.75077,0 -109.99959,-58.2039 -109.99959,-130.00041 C 146.00041,58.203898 195.24923,17.712357 256,17.712357 Z M 442.19077,512 H 69.809232 c -18.67159,0 -33.808411,-15.13682 -33.808411,-33.80841 0,-87.36657 98.495999,-188.6034 219.999179,-188.6034 121.50318,0 219.99918,101.23683 219.99918,188.6034 C 475.99918,496.86318 460.864,512 442.19077,512 Z"/>
</svg>`;

const BASE_CSS = `
html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; font-family: Arial, sans-serif; font-size: 18px; }
.sc-widget { position: relative; width: 100%; height: 100%; overflow: hidden; display: flex; align-items: center; }
.sc-track { display: flex; flex-wrap: nowrap; align-items: center; white-space: nowrap; will-change: transform; }
.sc-item { display: inline-flex; align-items: center; flex-shrink: 0; }
.sc-avatar-wrap { position: relative; width: 1.8em; height: 1.8em; flex-shrink: 0; margin-right: 0.4em; }
.sc-avatar-wrap > img:not(.sc-service-icon) { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; display: block; }
.sc-avatar-fallback { width: 100%; height: 100%; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #241e32; overflow: hidden; }
.sc-avatar-fallback svg { width: 70%; height: 70%; }
.sc-service-icon { position: absolute; width: 55%; height: 55%; right: -10%; bottom: -10%; border-radius: 3px; object-fit: cover; }
.sc-name { margin-right: 0.3em; font-weight: bold; }
.sc-amount { opacity: 0.9; }
.sc-sep { margin: 0 0.8em; opacity: 0.4; }
`;

export default class ScrollerWidget extends Widget {
	constructor(opts) {
		super(opts);
		this._items = [];
		this._offset = 0;
		this._setWidth = 0;
		this._animFrameId = null;
		this._lastTs = null;
		this._injectCSS('scBaseCSS', BASE_CSS);
		this._startAnimation();
	}


	_build() {
		this.container.classList.add('sc-widget');
		this._track = document.createElement('div');
		this._track.className = 'sc-track';
		this.container.appendChild(this._track);
	}

	destroy() {
		if(this._animFrameId !== null) {
			cancelAnimationFrame(this._animFrameId);
			this._animFrameId = null;
		}
		super.destroy();
	}

	// ── Settings ──────────────────────────────────────────────────────────────

	async init(initialData = {}) {
		await super.init(initialData);
		if(initialData.alerts) this.applyAlerts(initialData.alerts);
	}

	applySettings(settings) {
		super.applySettings(settings);
		this._rebuildTrack();
	}

	applyAlerts(alerts) {
		for(const alert of [...alerts].reverse()) {
			if(!alert.message) continue;
			this._pushAlert(alert.message);
		}
		this._rebuildTrack();
	}

	_generateCSS() {
		const s = this.settings;
		const textDecoration = [
			s.underlineText     ? 'underline'    : null,
			s.strikethroughText ? 'line-through' : null,
		].filter(Boolean).join(' ');

		const css = `
.sc-widget {
	${s.fontFamily    ? `font-family: '${s.fontFamily}';`       : ''}
	${s.fontSize      ? `font-size: ${s.fontSize}px;`           : ''}
	${s.letterSpacing ? `letter-spacing: ${s.letterSpacing}px;` : ''}
	${s.boldText      ? 'font-weight: bold;'                    : ''}
	${s.italicText    ? 'font-style: italic;'                   : ''}
	${textDecoration  ? `text-decoration: ${textDecoration};`   : ''}
	${s.uppercaseText ? 'text-transform: uppercase;'            : ''}
	${s.textColor     ? `color: ${s.textColor};`                : ''}
}
.sc-item {
	background: ${s.itemBg || 'transparent'};
	padding-top: ${s.paddingTop !== undefined ? s.paddingTop : 0}px;
	padding-bottom: ${s.paddingBottom !== undefined ? s.paddingBottom : 0}px;
	${(s.blurRadius > 0 || s.spreadRadius > 0) ? `filter: ${util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)};` : ''}
}
${s.extraCSS || ''}
`;
		this._setCSS('scStyle', css);
		this._updateFontLink();
	}

	_onAlert(data) {
		if(!data?.message) return;
		if(this.settings.donationsOnly && !(data.message.value > 0)) return;
		this._pushAlert(data.message);
		this._rebuildTrack();
	}

	_pushAlert(message) {
		const prepared = util.prepareMessage(message, {
			id: true,
			serviceId: true, serviceName: true, serviceIconUrl: true,
			avatarUrl: true, displayName: true,
			giftString: true, eventString: true,
		}, {
			giftTypes: this.giftTypes,
			eventTypes: this.eventTypes,
			serviceIcons: this.serviceIcons,
			user: this.userData,
		});
		const itemCount = this.settings.itemCount || 10;
		this._items.push(prepared);
		while(this._items.length > itemCount) this._items.shift();
	}

	_rebuildTrack() {
		this._track.innerHTML = '';
		if(!this._items.length) return;

		// Build the first two copies, then measure one set's width with float
		// precision to avoid integer-rounding drift accumulating into a visible jump.
		for(let copy = 0; copy < 2; copy++) {
			for(const item of this._items) {
				this._track.appendChild(this._buildItemElem(item));
			}
		}

		this._track.style.transform = 'none';
		const firstItem = this._track.children[0];
		const secondCopyFirst = this._track.children[this._items.length];
		this._setWidth = secondCopyFirst
			? secondCopyFirst.getBoundingClientRect().left - firstItem.getBoundingClientRect().left
			: this._track.scrollWidth / 2;

		// Add more copies until the track is wide enough to fill the container
		// with no gaps — needed when few items are shorter than the viewport.
		if(this._setWidth > 0) {
			const containerWidth = this.container.offsetWidth || 0;
			const needed = Math.ceil(containerWidth / this._setWidth) + 1;
			for(let copy = 2; copy < needed; copy++) {
				for(const item of this._items) {
					this._track.appendChild(this._buildItemElem(item));
				}
			}
		}

		while(this._offset < -this._setWidth) this._offset += this._setWidth;
		while(this._offset > 0) this._offset -= this._setWidth;

		this._track.style.transform = `translateX(${this._offset}px)`;
	}

	_buildItemElem(prepared) {
		const item = document.createElement('div');
		item.className = 'sc-item';

		const wrap = document.createElement('div');
		wrap.className = 'sc-avatar-wrap';
		if(prepared.avatarUrl) {
			const img = document.createElement('img');
			img.src = prepared.avatarUrl;
			img.alt = '';
			wrap.appendChild(img);
		} else {
			const fallback = document.createElement('div');
			fallback.className = 'sc-avatar-fallback';
			fallback.innerHTML = AVATAR_SVG;
			wrap.appendChild(fallback);
		}
		if(prepared.serviceIconUrl) {
			const si = document.createElement('img');
			si.src = prepared.serviceIconUrl;
			si.alt = prepared.serviceName || '';
			si.className = 'sc-service-icon';
			wrap.appendChild(si);
		}
		item.appendChild(wrap);

		const nameSpan = document.createElement('span');
		nameSpan.className = 'sc-name';
		nameSpan.textContent = prepared.displayName || '';
		item.appendChild(nameSpan);

		const amountText = prepared.giftString || prepared.eventString;
		if(amountText) {
			const amountSpan = document.createElement('span');
			amountSpan.className = 'sc-amount';
			amountSpan.textContent = amountText;
			item.appendChild(amountSpan);
		}

		const sep = document.createElement('span');
		sep.className = 'sc-sep';
		sep.textContent = this.settings.separator !== undefined ? this.settings.separator : '\u00b7';
		item.appendChild(sep);

		return item;
	}

	_startAnimation() {
		const frame = ts => {
			this._animFrameId = requestAnimationFrame(frame);
			if(this._lastTs === null) {
				this._lastTs = ts;
				return;
			}
			const dt = Math.min((ts - this._lastTs) / 1000, 0.1);
			this._lastTs = ts;

			if(!this._items.length || this._setWidth === 0) return;

			const speed = typeof this.settings.speed === 'number' ? this.settings.speed : 60;
			if(speed === 0) return;

			this._offset -= speed * dt;

			while(this._offset < -this._setWidth) this._offset += this._setWidth;
			while(this._offset > 0) this._offset -= this._setWidth;

			this._track.style.transform = `translateX(${this._offset}px)`;
		};
		this._animFrameId = requestAnimationFrame(frame);
	}
}
